import { useAtom, useSetAtom } from 'jotai';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { type CallBackProps, STATUS, type Step as TourStep } from 'react-joyride';
import { browserClickPositionAtom } from '~/atoms/atoms';
import { contextMenuDropDownAtom, recordTypeAtom } from '~/atoms/recorder-atoms';
import MantineJoyride from './common/MantineJoyride';
import { Logo } from '../common/Logo';
import { useComputedColorScheme } from '@mantine/core';
import {
  IconArrowNarrowDown,
  IconBrowser,
  IconClick,
  IconEdit,
  IconFocus2,
  IconPlayerPauseFilled,
  IconPlayerTrackNextFilled,
  IconRectangleVerticalFilled,
  IconSparkles,
  IconToggleRight,
} from '@tabler/icons-react';
import IconSingleClick from '../icons/IconSingleClick';
import Step from '../test/steps/step/Step';
import type { SelectedStep } from '~/server/logic/prisma/step-select';

const IconTitle = ({ icon, title }: { icon?: ReactNode; title: string }) => {
  return (
    <div className="flex items-center justify-center space-x-2">
      {icon}
      <span>{title}</span>
    </div>
  );
};

const StablyIcon = () => {
  const colorScheme = useComputedColorScheme();
  return (
    <div style={{ width: '24px', height: '24px' }}>
      <Logo color={colorScheme === 'dark' ? 'white' : 'black'} />
    </div>
  );
};

export const RecorderClickModeExplanation = () => (
  <div>
    <div className="text-center">By clicking here, you can switch between center and relative click modes</div>
    <div className="mt-4 flex w-full flex-col items-center justify-center">
      <div className="flex flex-col justify-center">
        <div className="flex items-center space-x-1">
          <IconFocus2
            size={18}
            className="w-5"
          />
          <span className="text-xs">Clicks will be centered on the element</span>
        </div>
        <div className="flex items-center space-x-1">
          <IconSingleClick className="w-5" />
          <span className="text-xs">Clicks will be relative to the click position</span>
        </div>
      </div>
    </div>
  </div>
);

export const TOUR_ID = {
  BROWSER_PANEL: 'browser-panel',
  EDITOR_PANEL: 'editor-panel',
  PREVIEW_BUTTON: 'preview-button',
  RECORDER_CURSOR: 'recorder-cursor',
  MOVE_CURSOR: 'move-cursor',
  PREVIEW_OP_BUTTON: 'preview-op-button',
  ADD_STEP: 'add-step',
  FLOATING_TOOLBAR: 'floating-toolbar',
  FLOATING_TOOLBAR_RECORD_TYPE: 'floating-toolbar-record-type',
  FLOATING_TOOLBAR_RECORD: 'floating-toolbar-record',
  FLOATING_TOOLBAR_RECORDER_CLICK_MODE_ACTION: 'floating-toolbar-recorder-click-mode-action',
  FLOATING_TOOLBAR_AI_ACTION: 'floating-toolbar-ai-action',
  BROWSER_VM: 'browser-vm-stream',
} as const;

// Quick fix for misplaced spotlight bug
// TODO: don't use split panes or something else to avoid this
const TRANSPARENT_OVERLAY_STYLE = {
  overlay: { backgroundColor: 'transparent' },
};

const createTourStep = (operationId: string): SelectedStep => ({
  id: 'joyride-step',
  stepOrder: 0,
  mode: 'AI_FALLBACK',
  description: 'This is a tour step',
  createdAt: new Date(),
  updatedAt: new Date(),
  testContentId: '',
  source: 'RECORDER',
  skip: false,
  testAccountId: null,
  autoHealConfig: null,
  omitReferenceActions: null,
  operations: [
    {
      id: operationId,
      order: 0,
      type: 'ACTION',
      pageAlias: 'page',
      skip: false,
      testStep: { id: 'joyride-step' },
      actionOp: {
        action: 'fill',
        targetV2: {
          name: 'Greet',
          locator: "getByPlaceholder('Greet')",
        },
        target2V2: null,
        value: 'Hi there!',
        valueType: 'STATIC',
        options: {},
      },
      // Null fields
      beforeScreenshot: null,
      actionScreenshot: null,
      afterScreenshot: null,
      assertionOp: null,
      aiActionOp: null,
      aiAssertionOp: null,
      uploadOp: null,
      defineVariableOp: null,
      receiveEmailOp: null,
      fetchRequestOp: null,
      waitOp: null,
      navigationOp: null,
      codeBlockOp: null,
      opensPageAlias: null,
      opensFileUploadDialog: null,
      authStateOp: null,
      specialLoginOp: null,
      componentCallOp: null,
      conditionalOp: null,
      endTestOp: null,
    },
  ],
});

const TOUR_STEPS: TourStep[] = [
  // Welcome step
  {
    title: (
      <IconTitle
        icon={<StablyIcon />}
        title="Welcome!"
      />
    ),
    target: 'body',
    content: "Welcome to Stably Test Editor! Let's take a quick tour to get you started",
    placement: 'center',
  },
  // Browser Panel
  {
    title: (
      <IconTitle
        icon={<IconBrowser />}
        title="Browser Panel"
      />
    ),
    target: `#${TOUR_ID.BROWSER_PANEL}`,
    content: 'You can view your website and interact with it by clicking or double-clicking anywhere',
    placement: 'left',
  },
  // Editor Panel
  {
    title: (
      <IconTitle
        icon={<IconEdit />}
        title="Editor Panel"
      />
    ),
    target: `#${TOUR_ID.EDITOR_PANEL}`,
    content: 'Any actions or assertions you record will be displayed here',
    placement: 'right',
  },
  // Recorder Cursor
  {
    title: (
      <IconTitle
        icon={<IconRectangleVerticalFilled className="scale-x-50" />}
        title="Recorder Cursor"
      />
    ),
    target: `#${TOUR_ID.RECORDER_CURSOR}`,
    content: 'This is the recorder cursor. It shows where the next recorded action will be added',
    placement: 'right',
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Moving the cursor
  {
    title: <IconTitle title="Moving the cursor" />,
    target: `#${TOUR_ID.EDITOR_PANEL}`,
    content: (
      <div className="pointer-events-none">
        <div>
          You can also move the cursor to any action by hovering over the bottom left corner of the action and clicking
          there
        </div>
        <div className="relative">
          <Step
            idx={0}
            testContentId=""
            step={createTourStep(TOUR_ID.MOVE_CURSOR)}
            isFirstNavOp={false}
          />
          {/* Hard coded arrow */}
          <div className="absolute bottom-0 left-0 mx-5 my-[18px] rotate-90">
            <IconArrowNarrowDown className="animate-bounce" />
          </div>
        </div>
      </div>
    ),
    placement: 'right',
    floaterProps: { styles: { arrow: { display: 'none' } } },
  },
  // Previewing to an action
  {
    title: <IconTitle title="Previewing to an action" />,
    target: `#${TOUR_ID.EDITOR_PANEL}`,
    content: (
      <div className="pointer-events-none">
        <div>To preview up to a specific action, hover over it and click on the run button</div>
        <div className="relative">
          <Step
            idx={0}
            testContentId=""
            step={createTourStep(TOUR_ID.PREVIEW_OP_BUTTON)}
            isFirstNavOp={false}
          />
          {/* Hard coded arrow */}
          <div className="absolute bottom-0 right-0 mx-5 my-[18px] -rotate-[135deg]">
            <IconArrowNarrowDown className="animate-bounce" />
          </div>
        </div>
      </div>
    ),
    placement: 'right',
    floaterProps: { styles: { arrow: { display: 'none' } } },
  },
  // Preview Button
  {
    title: (
      <IconTitle
        icon={<IconPlayerTrackNextFilled />}
        title="Preview the test"
      />
    ),
    target: `#${TOUR_ID.PREVIEW_BUTTON}`,
    content: 'Use this button to preview from the current cursor position to the end of the test',
    placement: 'bottom',
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Context Menu
  {
    title: (
      <IconTitle
        icon={<IconClick />}
        title="Dropdown Menu"
      />
    ),
    target: `#${TOUR_ID.BROWSER_VM}`,
    content: 'By right-clicking anywhere on the browser, you can access the dropdown menu to see more options',
    placement: 'left',
    data: { showDropdown: true },
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Floating Toolbar Record Type
  {
    title: (
      <IconTitle
        icon={<IconToggleRight />}
        title="Toggle Record Type"
      />
    ),
    target: `#${TOUR_ID.FLOATING_TOOLBAR_RECORD_TYPE}`,
    content: 'Use this toggle to switch the type of recording you want to make',
    placement: 'bottom',
    data: { recordType: 'assertion' },
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Pause Recording
  {
    title: (
      <IconTitle
        icon={<IconPlayerPauseFilled />}
        title="Pause Recording"
      />
    ),
    target: `#${TOUR_ID.FLOATING_TOOLBAR_RECORD}`,
    content: 'You can also pause or resume recording to exclude certain actions from your test',
    placement: 'bottom',
    data: { recordType: 'action' },
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Recorder Click Mode
  {
    title: (
      <IconTitle
        icon={<IconFocus2 />}
        title="Clicking Mode"
      />
    ),
    target: `#${TOUR_ID.FLOATING_TOOLBAR_RECORDER_CLICK_MODE_ACTION}`,
    content: <RecorderClickModeExplanation />,
    placement: 'bottom',
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // AI Action
  {
    title: (
      <IconTitle
        icon={<IconSparkles />}
        title="AI Action"
      />
    ),
    target: `#${TOUR_ID.FLOATING_TOOLBAR_AI_ACTION}`,
    content: 'And by clicking here, you can use Stably AI to generate an action or assertion for you',
    placement: 'bottom',
    styles: TRANSPARENT_OVERLAY_STYLE,
    spotlightPadding: 0,
  },
  // Goodbye
  {
    title: (
      <IconTitle
        icon={<StablyIcon />}
        title="All Set!"
      />
    ),
    target: 'body',
    content: "That's it! You're all set to start testing your website!",
    placement: 'center',
  },
];

export function DraftEditorTour({ run, onClose }: { run: boolean; onClose: () => void }) {
  const [browserClickPosition, setBrowserClickPosition] = useAtom(browserClickPositionAtom);
  const [openDropdown, setOpenDropdown] = useAtom(contextMenuDropDownAtom);
  const setRecordType = useSetAtom(recordTypeAtom);
  const dropdownShouldBeOpen = useRef(false);

  const resetState = useCallback(() => {
    setBrowserClickPosition(undefined);
    setOpenDropdown(false);
    setRecordType('action');
  }, [setBrowserClickPosition, setOpenDropdown, setRecordType]);

  const openContextMenu = useCallback(
    (target: string) => {
      const size = document.querySelector(target)?.getBoundingClientRect();
      if (size) {
        setBrowserClickPosition({
          x: { absolute: size.width / 2, ratio: 0.5 },
          y: { absolute: size.height / 4, ratio: 0.5 },
        });
      }
    },
    [setBrowserClickPosition],
  );

  const handleJoyrideCallback = ({ status, step }: CallBackProps) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      resetState();
      onClose();
    }

    if (step.data?.recordType) {
      setRecordType(step.data.recordType);
    }

    if (step.data?.showDropdown) {
      dropdownShouldBeOpen.current = true;
      openContextMenu(step.target.toString());
      setOpenDropdown(true);
    } else {
      dropdownShouldBeOpen.current = false;
      setBrowserClickPosition(undefined);
      setOpenDropdown(false);
    }
  };

  useEffect(() => {
    const originalOverflowState = document.body.style.overflow;
    if (run) {
      // Prevent scrolling when the tour is running
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflowState;
    };
  }, [run]);

  // It is still possible to close the dropdown even when the tour is running, so we need to handle this case
  useEffect(() => {
    if (dropdownShouldBeOpen.current && (!openDropdown || !browserClickPosition)) {
      openContextMenu(`#${TOUR_ID.BROWSER_VM}`);
      setOpenDropdown(true);
    }
  }, [browserClickPosition, openDropdown]);

  return (
    <MantineJoyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      disableCloseOnEsc
      disableOverlayClose
      hideBackButton // Hiding the back button as it breaks the tour on step 8 (show dropdown)
      run={run}
      steps={TOUR_STEPS}
      scrollOffset={window.innerHeight / 2}
    />
  );
}
